import { useCallback, useEffect, useRef, useState } from 'react'

interface UsePlayAudioReturn {
  playingId: string | null
  isPlaying: (id: string) => boolean
  play: (id: string, url: string) => Promise<void>
  stop: () => void
}

export function usePlayAudio(): UsePlayAudioReturn {
  const [playingId, setPlayingId] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const currentIdRef = useRef<string | null>(null)

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      setPlayingId(null)
    }
  }, [])

  const play = useCallback(
    async (id: string, url: string) => {
      // If there's a different audio playing, stop it first
      if (audioRef.current && currentIdRef.current !== id) {
        stop()
      }

      // If clicking the same audio that's currently loaded
      if (currentIdRef.current === id) {
        if (audioRef.current?.paused) {
          await audioRef.current.play()
          setPlayingId(id)
        } else {
          stop()
        }
        return
      }

      try {
        const audio = new Audio(url)
        audio.onended = () => {
          stop()
          currentIdRef.current = null
        }
        audioRef.current = audio
        currentIdRef.current = id
        setPlayingId(id)
        await audio.play()
      } catch (error) {
        stop()
        currentIdRef.current = null
        throw error
      }
    },
    [stop],
  )

  const isPlaying = useCallback((id: string) => playingId === id, [playingId])

  // Cleanup audio on unmount
  useEffect(
    () => () => {
      stop()
      currentIdRef.current = null
    },
    [stop],
  )

  return {
    playingId,
    isPlaying,
    play,
    stop,
  }
}
